/**
 * LoadingSpinner Component
 *
 * A reusable loading spinner component with configurable size and color.
 */

import { type CSSProperties } from 'react';

/**
 * Props for LoadingSpinner component
 */
export interface LoadingSpinnerProps {
  /** Size of the spinner (default: 'md') */
  size?: 'sm' | 'md' | 'lg';
  /** Color of the spinner (default: '#3b82f6') */
  color?: string;
  /** Additional CSS class name */
  className?: string;
  /** Accessible label for screen readers */
  label?: string;
}

/**
 * Size configurations for the spinner
 */
const sizeConfig = {
  sm: { width: '20px', height: '20px', borderWidth: '2px' },
  md: { width: '40px', height: '40px', borderWidth: '3px' },
  lg: { width: '60px', height: '60px', borderWidth: '4px' },
};

/**
 * LoadingSpinner displays an animated spinning indicator
 *
 * @example
 * ```tsx
 * <LoadingSpinner />
 * <LoadingSpinner size="lg" color="#ef4444" />
 * ```
 */
export const LoadingSpinner = ({
  size = 'md',
  color = '#3b82f6',
  className,
  label = 'Loading',
}: LoadingSpinnerProps) => {
  const sizeStyles = sizeConfig[size];

  const spinnerStyle: CSSProperties = {
    width: sizeStyles.width,
    height: sizeStyles.height,
    border: `${sizeStyles.borderWidth} solid #f3f4f6`,
    borderTop: `${sizeStyles.borderWidth} solid ${color}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  };

  return (
    <>
      <div
        style={spinnerStyle}
        className={className}
        role="status"
        aria-label={label}
        aria-live="polite"
      >
        <span style={srOnlyStyle}>{label}</span>
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

/**
 * Screen reader only styles
 */
const srOnlyStyle: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export default LoadingSpinner;
